import type { ScaleLinear, ScaleTime } from 'd3-scale'
import { stack, stackOffsetDiverging } from 'd3-shape'
import { useMemo } from 'react'

import { RECT_OPACITY } from '@/features/bidding/components/cascading/charts/constants'
import type { AreaGraphElement, FillFunction } from '@/features/bidding/components/cascading/charts/types'
import { getNextDatetime } from '@/features/bidding/components/cascading/charts/utils/getNextDatetime'

type BarRect = {
  key: string
  x: number
  y: number
  width: number
  height: number
  fill: string
  stroke: string
}

type Layer = {
  key: string
  rects: BarRect[]
}

type AreaGraphLayersProps = {
  data: AreaGraphElement[]
  layerKeys: string[]
  layerFill: FillFunction
  layerStroke?: (key: string) => string
  timeScale: ScaleTime<number, number>
  valueScale: ScaleLinear<number, number>
  /** Unique ID prefix for gradient definitions (prevents clashes with multiple charts) */
  id: string
  /** Index of currently hovered data point (for dimming other bars) */
  hoveredIndex?: number | null
}

/**
 * Renders stacked bar rects using D3 stack layout.
 * Each data point is rendered as an individual rect with a solid fill color,
 * avoiding gradient artifacts from the previous curveStepAfter approach.
 */
function AreaGraphLayers({
  data,
  layerKeys,
  layerFill,
  layerStroke,
  timeScale,
  valueScale,
  id,
  hoveredIndex,
}: Readonly<AreaGraphLayersProps>) {
  const datetimes = useMemo(() => data.map((d) => d.datetime), [data])

  const layers = useMemo(() => {
    if (data.length === 0) return []

    const stackGenerator = stack<AreaGraphElement>()
      .offset(stackOffsetDiverging)
      .value((d, key) => d.layerData[key] ?? 0)
      .keys(layerKeys)

    const stackedData = stackGenerator(data)

    return layerKeys.map((key, index) => {
      const layerData = stackedData[index]
      const stroke = layerStroke?.(key) ?? 'none'
      const fillFn = layerFill(key)

      const rects: BarRect[] = layerData
        .map((d, i) => {
          const bottom = d[0]
          const top = d[1]

          if (!Number.isFinite(bottom) || !Number.isFinite(top)) return null

          const height = Math.abs(valueScale(bottom) - valueScale(top))
          if (height === 0) return null

          const x = timeScale(d.data.datetime)
          const nextDt = getNextDatetime(datetimes, d.data.datetime)
          const width = timeScale(nextDt) - x

          return {
            key: `${id}-${key}-${i}`,
            x,
            y: Math.min(valueScale(bottom), valueScale(top)),
            width,
            height,
            fill: fillFn({ data: d.data }),
            stroke,
          }
        })
        .filter((rect): rect is BarRect => rect !== null)

      return { key, rects }
    }) as Layer[]
  }, [data, datetimes, id, layerKeys, layerFill, layerStroke, timeScale, valueScale])

  return (
    <g className="area-layers">
      {layers.map((layer) =>
        layer.rects.map((rect) => (
          <rect
            key={rect.key}
            fill={rect.fill}
            height={rect.height}
            opacity={RECT_OPACITY}
            shapeRendering="geometricPrecision"
            stroke={rect.stroke}
            strokeWidth={rect.stroke !== 'none' ? 1 : 0}
            style={{ cursor: 'pointer' }}
            width={rect.width}
            x={rect.x}
            y={rect.y}
          />
        )),
      )}

      {/* Dim overlay for non-hovered bars */}
      {hoveredIndex !== null &&
        hoveredIndex !== undefined &&
        datetimes.map((dt, i) => {
          if (i === hoveredIndex) return null
          const barX = timeScale(dt)
          const nextDt =
            datetimes[i + 1] ?? new Date(dt.getTime() + (datetimes[1]?.getTime() - datetimes[0]?.getTime()))
          const barWidth = timeScale(nextDt) - barX
          const [y2, y1] = valueScale.range()
          return (
            <rect
              key={`dim-${dt.getTime()}`}
              fill="white"
              height={y2 - y1}
              opacity={0.4}
              style={{ pointerEvents: 'none' }}
              width={barWidth}
              x={barX}
              y={y1}
            />
          )
        })}
    </g>
  )
}

export default AreaGraphLayers
